import { makeAutoObservable, runInAction } from "mobx";
import type { AnalyticsManager } from "services/analytics/analyticsManager";
import { type ComplianceClient } from "services/compliance/complianceClient";

// Matches ComplianceStore's own poll rhythm. Not reusing ComplianceStore itself: it also fetches
// the sumsub application and address verification result once compliance finishes, which belongs
// to the IDENTITY step, not here.
const POLL_INTERVAL_MS = 3_000;

export class ComplianceWaitStore {
  isFinished = false;
  isSuccessful = false;

  private _pollInterval?: ReturnType<typeof setInterval>;
  // See ComplianceCallStore for why: a run whose epoch has passed stops touching anything.
  private _epoch = 0;

  constructor(
    private readonly _client: ComplianceClient,
    private readonly _analyticsManager: AnalyticsManager,
  ) {
    makeAutoObservable(this);
  }

  get isRejected(): boolean {
    return this.isFinished && !this.isSuccessful;
  }

  get isReadyToSubmit(): boolean {
    return this.isFinished && this.isSuccessful;
  }

  init(): void {
    const epoch = ++this._epoch;
    this._startPolling(epoch);
  }

  dispose(): void {
    this._epoch += 1;
    this._clearPolling();
  }

  // No timeout: the application isn't submitted without a finished, successful check either way,
  // so there is nothing safer to fall back to. Polls until is_finished comes back true.
  private _startPolling(epoch: number): void {
    void this._poll(epoch);
    this._pollInterval = setInterval(() => {
      void this._poll(epoch);
    }, POLL_INTERVAL_MS);
  }

  private async _poll(epoch: number): Promise<void> {
    try {
      const data = await this._client.getComplianceResult();
      if (epoch !== this._epoch || this.isFinished) {
        // Either disposed, or a slower request from before the check finished landing after a
        // faster one already reported it done — the check can't un-finish, so this read is stale.
        return;
      }

      runInAction(() => {
        this.isFinished = data.isFinished;
        this.isSuccessful = data.isSuccessful;
      });
      this._analyticsManager.event("compliance-wait-status", {
        isFinished: data.isFinished,
        isSuccessful: data.isSuccessful,
      });

      if (data.isFinished) {
        this._clearPolling();
      }
    } catch {
      // Transient read failure — keep polling.
    }
  }

  private _clearPolling(): void {
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = undefined;
    }
  }
}
